// ===========================================
// Updated LibraryTab.js - Database Reading Component
// ===========================================
import React, { useEffect, useCallback } from 'react';
import { Book, RefreshCw } from 'lucide-react';
import SearchBar from './SearchBar';
import BookGrid from './BookGrid';
import LoadMoreTrigger from './LoadMoreTrigger';
import DatabaseStatus from './DatabaseStatus';
import { usePaginatedBooks } from '../hooks/usePaginatedBooks';
import { useResponsivePagination } from '../hooks/useResponsivePagination';
import { SORT_OPTIONS } from '../utils/constants';

const LibraryTab = ({ 
  books, 
  filteredBooks, 
  searchStore, 
  sortBy, 
  setSortBy, 
  onSearch, 
  onViewBook, 
  onDownloadBook,
  onSwitchTab,
  isBooksLoading,
  booksError,
  refreshBooks
}) => {
    
  // 🎯 RESPONSIVE BOOKS PER PAGE (Method 3)
  const booksPerPage = useResponsivePagination();
  
  const {
    paginatedBooks,
    isLoading,
    hasMore,
    loadMore,
    resetPagination,
    debugInfo
  } = usePaginatedBooks(filteredBooks, booksPerPage);

  // Reset pagination when filtered books change
  useEffect(() => {
    // Only reset if there are actual changes in the book list
    if (filteredBooks.length === 0) return; // Don't reset for empty states
    resetPagination();
  }, [filteredBooks, resetPagination]);

  // Handle refresh with state preservation
  const handleRefresh = useCallback(() => {
    // Call the refresh function but don't reset search/filters
    refreshBooks();
  }, [refreshBooks]);

  return (
    <div>
      {/* Header with Refresh Button and Database Status */}
      <div className="mb-6">
        {/* Mobile: Stack vertically, Desktop: Side by side */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">Library</h2>
            <DatabaseStatus />
          </div>
          <button
            onClick={handleRefresh}
            disabled={isBooksLoading}
            className="flex items-center justify-center gap-2 px-4 py-2 text-white transition-colors bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
          >
            <RefreshCw className={`w-4 h-4 ${isBooksLoading ? 'animate-spin' : ''}`} />
            <span className="sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Error State */}
      {booksError && (
        <div className="p-4 mb-6 border border-red-200 rounded-lg bg-red-50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-red-800">Error Loading Books</h3>
              <p className="text-sm text-red-600">Failed to load books from database. Please try again.</p>
            </div>
            <button
              onClick={handleRefresh}
              className="px-3 py-1 text-sm text-white transition-colors bg-red-600 rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Search Section */}
      <div className="mb-8">
        <SearchBar
          books={books}
          onSearch={onSearch}
          searchStore={searchStore}
        />
      </div>

      {/* Search Results Info */}
      {searchStore.searchTerm && (
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Found {filteredBooks.length} result{filteredBooks.length !== 1 ? 's' : ''} for "<strong>{searchStore.searchTerm}</strong>"
              {paginatedBooks.length < filteredBooks.length && (
                <span className="text-gray-500"> (showing {paginatedBooks.length})</span>
              )}
            </p>
            
            {/* Sort Options */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value={SORT_OPTIONS.RELEVANCE}>Relevance</option>
                <option value={SORT_OPTIONS.TITLE}>Title (A-Z)</option>
                <option value={SORT_OPTIONS.AUTHOR}>Author (A-Z)</option>
                <option value={SORT_OPTIONS.DATE_DESC}>Newest First</option>
                <option value={SORT_OPTIONS.DATE_ASC}>Oldest First</option>
                <option value={SORT_OPTIONS.DOWNLOADS}>Most Downloaded</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Debug Info */}
      {process.env.NODE_ENV === 'development' && debugInfo && (
        <div className="p-3 mb-4 font-mono text-xs text-gray-700 bg-gray-100 rounded-lg">
          <strong>Debug:</strong> Page {debugInfo.currentPage}/{debugInfo.totalPages} | 
          Showing {debugInfo.showingBooks}/{debugInfo.totalBooks} books | 
          Has more: {debugInfo.hasMore.toString()} | 
          Loading: {debugInfo.isLoading.toString()}
        </div>
      )}

      {/* Books Grid with Infinite Scroll */}
      {isBooksLoading && paginatedBooks.length === 0 ? (
        /* Initial Loading State */
        <div className="py-16 text-center">
          <RefreshCw className="w-16 h-16 mx-auto mb-4 text-gray-400 animate-spin" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">Loading Books...</h3>
          <p className="text-gray-600">Fetching books from database</p>
        </div>
      ) : paginatedBooks.length > 0 ? (
        <>
          <BookGrid
            books={paginatedBooks}
            onViewBook={onViewBook}
            onDownloadBook={onDownloadBook}
            isLoading={isLoading}
          />
          
          {/* Load More Trigger for Infinite Scroll */}
          {hasMore && (
            <LoadMoreTrigger
              onLoadMore={loadMore}
              isLoading={isLoading}
              hasMore={hasMore}
            />
          )}
        </>
      ) : (
        /* Empty State */
        <div className="py-16 text-center">
          <div className="max-w-md mx-auto">
            <Book className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="mb-2 text-lg font-medium text-gray-900">
              {searchStore.searchTerm ? 'No books found' : 'No books available'}
            </h3>
            <p className="mb-6 text-gray-600">
              {searchStore.searchTerm 
                ? 'Try adjusting your search terms or filters' 
                : 'Be the first to upload a book to the library!' 
              }
            </p>
            <div className="flex justify-center gap-3">
              {searchStore.searchTerm && (
                <button
                  onClick={() => {
                    searchStore.setSearchTerm('');
                    onSearch('');
                  }}
                  className="px-4 py-2 text-white transition-colors bg-gray-600 rounded-lg hover:bg-gray-700"
                >
                  Clear Search
                </button>
              )}
              <button
                onClick={() => onSwitchTab('upload')}
                className="px-4 py-2 text-white transition-colors bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Upload Books
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LibraryTab;